import { GoogleGenAI, HarmCategory, HarmBlockThreshold } from "@google/genai";

const SYSTEM_INSTRUCTION = `
You are an advanced AI specialized in transcribing visual mind maps into structured text. 
Your goal is to accurately capture the hierarchy, relationships, and content of the mind map provided in the image.

Rules:
1. Output strictly valid Markdown.
2. Use a level 1 Header (# Title) for the central node/topic of the mind map.
3. Use nested unordered lists (- Item) to represent branches. 
4. Indentation must strictly follow the visual hierarchy (e.g., Main Branch -> Sub Branch -> Leaf).
5. Do not include any conversational text, introductory phrases ("Here is the markdown..."), or code block delimiters (\`\`\`). Just the raw Markdown content.
6. If the mind map has directional arrows implying a process flow that isn't a strict tree, try to represent it logically as a list or use bold text to indicate special connections if necessary, but prioritize the tree structure.
7. Ignore purely decorative elements.
8. Support all languages (e.g., Japanese, English, Spanish). Transcribe the text exactly as it appears in the image.
`;

export const convertImageToMarkdown = async (base64Image: string, mimeType: string): Promise<string> => {
  try {
    const apiKey = process.env.API_KEY || (import.meta as any).env?.VITE_GEMINI_API_KEY || (import.meta as any).env?.GEMINI_API_KEY;
    if (!apiKey) {
      throw new Error("API Key is missing. Please ensure API key is configured.");
    }

    const ai = new GoogleGenAI({ apiKey });

    const response = await ai.models.generateContent({
      model: 'gemini-3.7-flash',
      contents: {
        parts: [
          {
            inlineData: {
              data: base64Image,
              mimeType: mimeType,
            },
          },
          {
            text: "Transcribe this mind map into a structured Markdown list. Keep the text in its original language.",
          },
        ],
      },
      config: {
        systemInstruction: SYSTEM_INSTRUCTION,
        temperature: 0.1, // Low temperature for more deterministic/accurate transcription
        safetySettings: [
          { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_ONLY_HIGH },
          { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_ONLY_HIGH },
          { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_ONLY_HIGH },
          { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_ONLY_HIGH },
        ]
      },
    });

    const text = response.text;

    if (!text || !text.trim()) {
      const candidate = response.candidates?.[0];
      if (candidate?.finishReason) {
        throw new Error(`Analysis blocked. Reason: ${candidate.finishReason}`);
      }
      throw new Error("The model returned an empty response. The image might be unclear.");
    }

    return text;
  } catch (error: any) {
    console.error("Gemini API Error:", error);
    // Propagate the specific error message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to process image due to an unexpected error.");
  }
};
